import { useEffect, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from './cn.js';

/// Um traço: os pontos `[x, y]` de quando encostou até soltar, relativos à LARGURA da área — assim
/// a assinatura se redesenha igual em qualquer tamanho de tela, sem distorcer.
export type PadStroke = Array<[number, number]>;
export type PadPointer = 'touch' | 'pen' | 'mouse';

const MIN_HEIGHT = 150;

/// A área de assinar com o dedo, a caneta do tablet ou o mouse.
///
/// Os traços moram em quem usa a área (`strokes`), não no desenho: ampliar a área, girar o celular
/// ou ir para a tela cheia redesenha o que já foi feito, em vez de apagar. A alça de baixo aumenta
/// a altura; `fill` ocupa todo o espaço do pai, para a tela cheia.
export function SignaturePad({
  strokes,
  onChange,
  label = 'Área de assinatura',
  height = 220,
  onHeightChange,
  fill = false,
  className,
}: {
  strokes: readonly PadStroke[];
  onChange: (strokes: PadStroke[], pointer: PadPointer) => void;
  label?: string | undefined;
  height?: number | undefined;
  onHeightChange?: ((height: number) => void) | undefined;
  fill?: boolean | undefined;
  className?: string | undefined;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<PadStroke | null>(null);
  const latest = useRef(strokes);
  // O observador de tamanho e o traço em andamento leem daqui a lista mais recente.
  useLayoutEffect(() => {
    latest.current = strokes;
  });

  useEffect(() => {
    const canvas = canvasRef.current!;
    const draw = () => paint(canvas, latest.current);
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => paint(canvasRef.current!, strokes), [strokes]);

  function point(event: ReactPointerEvent<HTMLCanvasElement>): [number, number] | null {
    const box = event.currentTarget.getBoundingClientRect();
    if (!box.width) return null;
    const clamp = (value: number, max: number) => Math.min(max, Math.max(0, value));
    return [
      clamp((event.clientX - box.left) / box.width, 1),
      clamp((event.clientY - box.top) / box.width, box.height / box.width),
    ];
  }

  function pointerOf(event: ReactPointerEvent): PadPointer {
    return event.pointerType === 'touch' || event.pointerType === 'pen'
      ? event.pointerType
      : 'mouse';
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border-[1.5px] border-dashed border-surface-border bg-surface',
        fill && 'min-h-[220px] flex-1',
        className,
      )}
      style={fill ? undefined : { height: Math.max(MIN_HEIGHT, height) }}
    >
      {/* A linha e o X de onde assinar, como no papel. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bottom-[28%] left-[5%] text-base text-content-muted"
      >
        ✕
      </span>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-[8%] bottom-[28%] border-b border-surface-border"
      />
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        className="block size-full cursor-crosshair touch-none text-content"
        onPointerDown={(event) => {
          const start = point(event);
          if (!start) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          drawing.current = [start];
          onChange([...strokes, drawing.current], pointerOf(event));
        }}
        onPointerMove={(event) => {
          const next = point(event);
          if (!drawing.current || !next) return;
          drawing.current.push(next);
          paint(event.currentTarget, latest.current);
        }}
        onPointerUp={(event) => {
          if (!drawing.current) return;
          drawing.current = null;
          onChange([...latest.current], pointerOf(event));
        }}
        onPointerCancel={() => {
          drawing.current = null;
        }}
      />
      {fill || !onHeightChange ? null : (
        <Grip height={Math.max(MIN_HEIGHT, height)} onHeightChange={onHeightChange} />
      )}
    </div>
  );
}

/// Arrastar a alça para baixo aumenta a área sem apagar o que já foi assinado; as setas também
/// servem, para quem não arrasta.
function Grip({
  height,
  onHeightChange,
}: {
  height: number;
  onHeightChange: (height: number) => void;
}) {
  const start = useRef<{ y: number; height: number } | null>(null);
  const limit = (value: number) =>
    Math.round(Math.max(MIN_HEIGHT, Math.min(window.innerHeight * 0.8, value)));
  return (
    <button
      type="button"
      aria-label="Arraste para aumentar a área de assinatura"
      className="absolute inset-x-0 bottom-0 grid h-6 cursor-ns-resize touch-none place-items-center bg-gradient-to-b from-transparent to-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        start.current = { y: event.clientY, height };
      }}
      onPointerMove={(event) => {
        if (start.current)
          onHeightChange(limit(start.current.height + event.clientY - start.current.y));
      }}
      onPointerUp={() => {
        start.current = null;
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') onHeightChange(limit(height + 40));
        else if (event.key === 'ArrowUp') onHeightChange(limit(height - 40));
        else return;
        event.preventDefault();
      }}
    >
      <span aria-hidden="true" className="h-1 w-10 rounded-full bg-surface-border" />
    </button>
  );
}

/// Redesenha tudo na resolução real da tela: nítido em tela de alta densidade.
function paint(canvas: HTMLCanvasElement, strokes: readonly PadStroke[]): void {
  const box = canvas.getBoundingClientRect();
  const context = canvas.getContext('2d');
  if (!context || !box.width) return;
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.round(box.width * ratio);
  canvas.height = Math.round(box.height * ratio);
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, box.width, box.height);
  context.strokeStyle = getComputedStyle(canvas).color;
  context.fillStyle = context.strokeStyle;
  context.lineWidth = 2.4;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (const stroke of strokes) {
    const [first, ...rest] = stroke;
    if (!first) continue;
    if (!rest.length) {
      context.beginPath();
      context.arc(first[0] * box.width, first[1] * box.width, 1.4, 0, Math.PI * 2);
      context.fill();
      continue;
    }
    context.beginPath();
    context.moveTo(first[0] * box.width, first[1] * box.width);
    for (const [x, y] of rest) context.lineTo(x * box.width, y * box.width);
    context.stroke();
  }
}
